"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/components/AuthProvider";
import { useModal } from "@/components/modals/ModalProvider";
import { getSupabaseBrowserClient } from "@/lib/supabase/browser";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { withTimeout } from "@/lib/withTimeout";
import { getPostLoginRedirect, getSupportDestinationForRole, isAdminDestinationPath } from "@/lib/auth/redirects";
import {
  clearAuthIntent,
  consumeAuthIntent,
  sanitizeAuthRedirectPath,
} from "@/lib/auth/authIntent";
import { isBlockedAccountStatus, normalizeAccountStatus } from "@/lib/accountDeletion/status";
import {
  GENERIC_INVALID_CREDENTIALS_MESSAGE,
  isGenericInvalidCredentialsError,
  suppressAuthUiResetForCredentialsError,
} from "@/lib/auth/loginErrors";
import {
  getRequestedPathFromCurrentUrl,
  readClientRedirectState,
} from "@/lib/auth/clientRedirectState";
import { buildOAuthCallbackUrl, logOAuthStart } from "@/lib/auth/oauthRedirect";
import {
  beginExplicitLoginIntent,
  clearExplicitLoginIntent,
  confirmExplicitPasswordLogin,
  isExplicitLoginCorrelationRequired,
} from "@/lib/auth/explicitLoginIntent";
import {
  authErrorClassName,
  authForgotLinkClassName,
  authGoogleButtonClassName,
  authInputClassName,
  authLabelClassName,
  authPrimaryButtonClassName,
  authSwitchLinkClassName,
} from "@/components/auth/authFormStyles";

const MISSING_CREDENTIALS_MESSAGE = "Enter your email and password to sign in.";
const OAUTH_CALLBACK_ERROR_MESSAGE =
  "We couldn't finish Google sign-in. Please try again. If it keeps happening, clear localhost cookies and try once more.";
const BUSINESS_ACCOUNT_EXISTS_MESSAGE =
  "This Google account is already registered as a business. Use the business login page or use a different email for your customer account.";
const OAUTH_CALLBACK_ERROR_CODES = new Set([
  "magic_link_expired",
  "auth_callback_failed",
  "session_missing",
]);

export default function CustomerLoginForm({
  next: nextOverride = null,
  initialEmail = "",
  context = null,
  onSuccess = null,
  onSwitchToSignup = null,
}) {
  const {
    supabase,
    loadingUser,
    beginAuthAttempt,
    endAuthAttempt,
    authAttemptId,
  } = useAuth();
  const modal = useModal();
  const searchParams = useSearchParams();
  const authDiagEnabled = process.env.NEXT_PUBLIC_AUTH_DIAG === "1";
  const debugAuth = process.env.NEXT_PUBLIC_DEBUG_AUTH === "1";

  const [email, setEmail] = useState(() => String(initialEmail || "").trim().toLowerCase());
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const errorId = useId();
  const [loading, setLoading] = useState(false);
  const attemptIdRef = useRef(0);
  const refreshControllerRef = useRef(null);
  const authTimeoutMs = 30000;
  const profileTimeoutMs = 15000;
  const refreshTimeoutMs = 15000;
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.replace(/\/+$/, "");
  const isStaffInviteContext = context === "staff-invite";

  useEffect(() => {
    const normalized = String(initialEmail || "").trim().toLowerCase();
    if (normalized) setEmail(normalized);
  }, [initialEmail]);

  const logAuthRequest = useCallback(
    (payload) => {
      if (!authDiagEnabled && !debugAuth) return;
      console.log("[AUTH_DEBUG]", payload);
    },
    [authDiagEnabled, debugAuth]
  );

  const logPasswordAuthBoundary = useCallback((event, payload = {}) => {
    if (process.env.NODE_ENV === "production" && !authDiagEnabled && !debugAuth) return;
    console.info("[AUTH_PASSWORD_BOUNDARY]", {
      component: "CustomerLoginForm",
      event,
      pathname: typeof window !== "undefined" ? window.location.pathname : null,
      search: typeof window !== "undefined" ? window.location.search : null,
      activeAuthMode: "customer_password",
      ...payload,
    });
  }, [authDiagEnabled, debugAuth]);

  const isTimeoutError = useCallback((err) => {
    const name = err?.name || "";
    const message = String(err?.message || "").toLowerCase();
    return (
      name === "TimeoutError" ||
      name === "AbortError" ||
      message.includes("timed out")
    );
  }, []);

  const getNextParam = useCallback(() => {
    const rawNext =
      typeof nextOverride === "string" && nextOverride.trim()
        ? nextOverride.trim()
        : searchParams?.get("next") ||
            searchParams?.get("returnUrl") ||
            searchParams?.get("callbackUrl") ||
            getRequestedPathFromCurrentUrl();
    if (!rawNext) return null;
    return sanitizeAuthRedirectPath(rawNext, null);
  }, [nextOverride, searchParams]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      console.info("[auth-next] form received next:", getNextParam() || null);
    }
  }, [getNextParam]);

  useEffect(() => {
    const authError = (searchParams?.get("auth") || "").trim();
    if (authError === "invalid_credentials") {
      setError(GENERIC_INVALID_CREDENTIALS_MESSAGE);
      return;
    }
    if (authError === "business_account_exists") {
      setError(BUSINESS_ACCOUNT_EXISTS_MESSAGE);
      return;
    }
    if (OAUTH_CALLBACK_ERROR_CODES.has(authError)) {
      setError(OAUTH_CALLBACK_ERROR_MESSAGE);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!attemptIdRef.current) return;
    if (authAttemptId === attemptIdRef.current) return;
    attemptIdRef.current = 0;
    setLoading(false);
  }, [authAttemptId]);

  useEffect(() => {
    return () => {
      if (refreshControllerRef.current) {
        refreshControllerRef.current.abort();
        refreshControllerRef.current = null;
      }
    };
  }, []);

  async function handleLogin(event) {
    event.preventDefault();
    if (loading || loadingUser) return;
    setError("");
    const client = supabase ?? getSupabaseBrowserClient();
    const normalizedEmail = String(email || "").trim();
    const hasEmail = Boolean(normalizedEmail);
    const hasPassword = Boolean(password);

    logPasswordAuthBoundary("submit", {
      submitEventFired: true,
      emailPresent: hasEmail,
      passwordPresent: hasPassword,
    });

    if (!hasEmail || !hasPassword) {
      logPasswordAuthBoundary("blocked_missing_credentials", {
        submitEventFired: true,
        emailPresent: hasEmail,
        passwordPresent: hasPassword,
      });
      setError(MISSING_CREDENTIALS_MESSAGE);
      return;
    }

    if (authDiagEnabled) {
      console.log("[AUTH_DIAG] customer login submit", {
        route: typeof window !== "undefined" ? window.location.pathname : null,
        hasSupabaseAuth: Boolean(supabase?.auth),
        hasClientAuth: Boolean(client?.auth),
        hasModalContext: typeof modal?.openModal === "function",
      });
    }

    if (!client || !client.auth) {
      setError("Auth is unavailable. Please refresh and try again.");
      return;
    }

    const attemptId = beginAuthAttempt("customer_login");
    attemptIdRef.current = attemptId;
    setLoading(true);

    const isStale = () => attemptIdRef.current !== attemptId;
    const finishAttempt = (result) => {
      endAuthAttempt(attemptId, result);
      if (attemptIdRef.current !== attemptId) return;
      attemptIdRef.current = 0;
      setLoading(false);
    };

    try {
      const signInStart = typeof performance !== "undefined" ? performance.now() : Date.now();
      logPasswordAuthBoundary("signInWithPassword:start", {
        submitEventFired: true,
        emailPresent: hasEmail,
        passwordPresent: hasPassword,
      });
      const explicitLoginIntent = beginExplicitLoginIntent("password");
      const signInResult = await withTimeout(
        client.auth.signInWithPassword({ email: normalizedEmail, password }),
        authTimeoutMs,
        `Login request timed out after ${Math.round(authTimeoutMs / 1000)}s`
      );
      const signInDurationMs = Math.round(
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
          signInStart
      );
      logAuthRequest({
        label: "supabase.signInWithPassword",
        url: supabaseUrl ? `${supabaseUrl}/auth/v1/token` : null,
        method: "POST",
        timeoutMs: authTimeoutMs,
        durationMs: signInDurationMs,
        status: signInResult?.error?.status ?? null,
        error: signInResult?.error?.message ?? null,
      });
      const { data, error: signInError } = signInResult;

      if (isStale()) return;
      if (signInError) {
        clearExplicitLoginIntent(explicitLoginIntent?.id);
        throw signInError;
      }
      suppressAuthUiResetForCredentialsError();

      const session = data?.session;
      if (!session) {
        setError("Login succeeded but no session was returned. Try again.");
        return;
      }
      if (
        !confirmExplicitPasswordLogin(explicitLoginIntent, session) &&
        isExplicitLoginCorrelationRequired()
      ) {
        setError("We couldn't confirm this sign-in. Please try again.");
        return;
      }

      const { data: sessionData, error: sessionError } = await client.auth.getSession();
      if (sessionError) throw sessionError;
      if (!sessionData?.session) throw new Error("No session after login.");

      const profileStart = typeof performance !== "undefined" ? performance.now() : Date.now();
      const profileResult = await withTimeout(
        client
          .from("users")
          .select("role,is_internal,account_status")
          .eq("id", session.user.id)
          .single(),
        profileTimeoutMs,
        `Profile request timed out after ${Math.round(profileTimeoutMs / 1000)}s`
      );
      const profileDurationMs = Math.round(
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
          profileStart
      );
      logAuthRequest({
        label: "supabase.profile.role",
        url: supabaseUrl ? `${supabaseUrl}/rest/v1/users` : null,
        method: "GET",
        timeoutMs: profileTimeoutMs,
        durationMs: profileDurationMs,
        status: profileResult?.error?.status ?? null,
        error: profileResult?.error?.message ?? null,
      });
      const { data: profile, error: profileError, status: profileStatus } = profileResult;

      if (isStale()) return;
      if (profileError && profileStatus !== 406) {
        console.warn("Customer login role lookup failed", profileError);
      }
      const accountStatus = normalizeAccountStatus(profile?.account_status);
      if (isBlockedAccountStatus(accountStatus)) {
        suppressAuthUiResetForCredentialsError();
        setError(GENERIC_INVALID_CREDENTIALS_MESSAGE);
        try {
          await client.auth.signOut({ scope: "local" });
        } catch {}
        return;
      }

      const normalizedRole =
        typeof profile?.role === "string" ? profile.role.trim().toLowerCase() : "";
      const requestedPath = getNextParam();
      if (process.env.NODE_ENV !== "production") {
        console.info("[auth-next] auth submit next:", requestedPath || "/");
      }
      const intentPath =
        requestedPath ||
        consumeAuthIntent({
          role: "customer",
          fallbackPath: "/",
        });
      try {
        if (refreshControllerRef.current) {
          refreshControllerRef.current.abort();
        }
        const refreshController = new AbortController();
        refreshControllerRef.current = refreshController;

        const refreshStart = typeof performance !== "undefined" ? performance.now() : Date.now();
        const res = await fetchWithTimeout("/api/auth/refresh", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            access_token: session?.access_token,
            refresh_token: session?.refresh_token,
          }),
          timeoutMs: refreshTimeoutMs,
          signal: refreshController.signal,
        });
        const refreshDurationMs = Math.round(
          (typeof performance !== "undefined" ? performance.now() : Date.now()) -
            refreshStart
        );
        logAuthRequest({
          label: "next.auth.refresh",
          url: "/api/auth/refresh",
          method: "POST",
          timeoutMs: refreshTimeoutMs,
          durationMs: refreshDurationMs,
          status: res?.status ?? null,
          error: res?.ok ? null : `HTTP ${res.status}`,
        });

        if (isStale()) {
          refreshControllerRef.current = null;
          return;
        }
        const refreshed = res.headers.get("x-auth-refresh-user") === "1";
        if (!refreshed) {
          refreshControllerRef.current = null;
          setError("We couldn't finish signing you in. Please try again.");
          return;
        }
        refreshControllerRef.current = null;
      } catch (err) {
        refreshControllerRef.current = null;
        if (!isTimeoutError(err)) {
          console.error("Auth refresh call failed", err);
        }
        setError(
          isTimeoutError(err)
            ? "Session persistence timed out. Please check your connection and try again."
            : "We couldn't finish signing you in. Please try again."
        );
        return;
      }

      // The server has the refreshed session and can resolve canonical admin
      // membership even when the actor's AAL1 RLS cannot read the role table.
      let isAdmin = false;
      try {
        const membership = await fetchWithTimeout("/api/account/admin-membership", {
          method: "GET",
          credentials: "include",
          timeoutMs: profileTimeoutMs,
        });
        if (membership.ok) {
          isAdmin = (await membership.json())?.hasAdminMembership === true;
        }
      } catch {
        // A failed membership lookup must never grant administrator routing.
      }
      if (isStale()) return;

      const roleForRedirect = isAdmin
        ? "admin"
        : normalizedRole === "business"
          ? "business"
          : "customer";
      const dest = getPostLoginRedirect({
        role: roleForRedirect,
        requestedPath: getSupportDestinationForRole(requestedPath, roleForRedirect),
        fallbackPath: requestedPath ? null : getSupportDestinationForRole(intentPath, roleForRedirect),
      });
      if (process.env.NODE_ENV !== "production") {
        console.info("[auth-next] login success next:", requestedPath || null);
        console.info("[auth-next] login success fallback:", requestedPath ? null : intentPath || null);
        console.info("[auth-next] final redirect destination:", dest);
        console.info("[AUTH_REDIRECT_TRACE] customer_login_submit", {
          role: roleForRedirect,
          requestedPath,
          intentPath,
          chosenDestination: dest,
          persistedRedirectState: readClientRedirectState(),
        });
      }

      const successResult = await onSuccess?.(dest, { isAdmin });
      if (successResult?.handledRedirect) return;
      if (isAdmin) {
        if (process.env.NODE_ENV !== "production") {
          console.info("[auth-next] post-login replace:", dest);
        }
        window.location.replace(dest);
        return;
      }
      if (process.env.NODE_ENV !== "production") {
        console.info("[auth-next] post-login replace:", dest);
      }
      window.location.replace(dest);
    } catch (err) {
      clearExplicitLoginIntent();
      if (isStale()) return;
      if (!isGenericInvalidCredentialsError(err) && !isTimeoutError(err)) {
        console.error("Customer login failed", err);
      }
      setError(
        isTimeoutError(err)
          ? "Login request timed out. Please check your connection and try again."
          : isGenericInvalidCredentialsError(err)
            ? GENERIC_INVALID_CREDENTIALS_MESSAGE
            : "Login failed. Please try again."
      );
    } finally {
      finishAttempt("password");
    }
  }

  async function handleGoogleLogin(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setError("");

    let attemptId = 0;
    try {
      const client = supabase ?? getSupabaseBrowserClient();
      if (!client || !client.auth) {
        setError("Auth is unavailable. Please refresh and try again.");
        return;
      }

      attemptId = beginAuthAttempt("customer_oauth");
      attemptIdRef.current = attemptId;
      setLoading(true);

      const queryNext = getNextParam();
      const storedIntent = queryNext ? null : sanitizeAuthRedirectPath(
        consumeAuthIntent({ role: "customer", fallbackPath: "/" }), "/"
      );
      // Session-stored intent is only a fallback. A prior admin navigation must
      // not become an explicit admin request for a later customer sign-in.
      const oauthNext = queryNext ||
        (storedIntent !== "/" && !isAdminDestinationPath(storedIntent) ? storedIntent : null);

      const currentOrigin = window.location.origin;
      const explicitLoginIntent = beginExplicitLoginIntent("oauth");
      const redirectTo = buildOAuthCallbackUrl({
        currentOrigin,
        next: oauthNext,
        intent: "customer",
        loginIntentId: explicitLoginIntent?.id,
      });
      const loginHint = isStaffInviteContext
        ? String(initialEmail || email || "").trim().toLowerCase()
        : "";
      logOAuthStart({ provider: "google", redirectTo, currentOrigin });

      const { error: oauthError } = await client.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo,
          ...(loginHint ? { queryParams: { login_hint: loginHint } } : {}),
        },
      });

      if (attemptIdRef.current !== attemptId) return;
      if (oauthError) {
        clearExplicitLoginIntent();
        clearAuthIntent();
        setError(oauthError.message);
      }
    } catch (err) {
      clearAuthIntent();
      console.error("Customer OAuth login failed", err);
      setError("Login failed. Please try again.");
    } finally {
      if (attemptId) {
        const finished = endAuthAttempt(attemptId, "oauth");
        if (finished) {
          attemptIdRef.current = 0;
          setLoading(false);
        }
      }
    }
  }

  // Only the missing-credentials error belongs to specific fields; credential failures stay form-level.
  const missingCredentials = error === MISSING_CREDENTIALS_MESSAGE;
  const emailInvalid = missingCredentials && !String(email || "").trim();
  const passwordInvalid = missingCredentials && !password;

  return (
    <>
      <form onSubmit={handleLogin}>
        <div className="mb-4 space-y-2">
          <label htmlFor="customer-login-email" className={authLabelClassName}>Email</label>
          <input
            id="customer-login-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={emailInvalid || undefined}
            aria-describedby={emailInvalid ? errorId : undefined}
            className={authInputClassName}
            disabled={loading || loadingUser}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="customer-login-password" className={authLabelClassName}>Password</label>
          <input
            id="customer-login-password"
            name="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={passwordInvalid || undefined}
            aria-describedby={passwordInvalid ? errorId : undefined}
            className={authInputClassName}
            disabled={loading || loadingUser}
          />
          <div className="mt-3 pb-1 text-right">
            <Link href="/auth/forgot-password" className={authForgotLinkClassName}>
              Forgot password?
            </Link>
          </div>
        </div>

        <div className="pt-3">
          {error ? (
            <div id={errorId} role="alert" className={authErrorClassName}>
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loading || loadingUser}
            className={authPrimaryButtonClassName}
          >
            {loading ? "Logging in..." : "Log in"}
          </button>
        </div>
      </form>

      <div className="mt-4">
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={loading || loadingUser}
          className={authGoogleButtonClassName}
        >
          <img src="/google-icon.svg" className="h-5 w-5" alt="Google" />
          Continue with Google
        </button>
        {isStaffInviteContext && initialEmail ? (
          <p className="mt-2 text-xs leading-5 text-slate-500">
            Choose the Google account for {String(initialEmail || "").trim().toLowerCase()}.
          </p>
        ) : null}
      </div>

      <p className="mt-4 text-center text-sm text-slate-700">
        {isStaffInviteContext ? "Need an account for this invite?" : "New to YourBarrio?"}{" "}
        <button
          type="button"
          onClick={() => onSwitchToSignup?.() || modal?.openModal?.("customer-signup")}
          className={authSwitchLinkClassName}
        >
          Create an account
        </button>
      </p>
    </>
  );
}
